import type { SyncOp } from '@ims/core'
import {
  batchOp,
  insertOp,
  newBrand,
  newCategory,
  newMovement,
  newProduct,
  newUnit,
  updateOp,
} from '../rows.ts'
import { mergeOptions, readOptions, type VariantOptions } from '../variants.ts'
import type { ImportPlan, ParsedProduct } from './validate.ts'

export interface ChunkContext {
  tenantId: string
  /** Where opening stock goes; null = no opening stock is written. */
  locationId: string | null
  /** New category names (lowercased) the user agreed to create. */
  createCategories: ReadonlySet<string>
  /** New brand names (lowercased) the user agreed to create. */
  createBrands?: ReadonlySet<string>
  now?: string
}

/**
 * Turns a plan into commits of at most `chunkSize` products. Each chunk is one
 * local commit, so one atomic sync_push tx: a product and its opening stock are
 * always in the same chunk. New units and categories go in the first chunk.
 */
export function planToChunks(plan: ImportPlan, ctx: ChunkContext, chunkSize = 100): SyncOp[][] {
  const rowCtx = { tenantId: ctx.tenantId, now: ctx.now }
  const categoryIds = new Map<string, string>()
  const unitIds = new Map<string, string>()
  const first: SyncOp[] = []
  for (const u of plan.newUnits) {
    if (!u.create) continue
    const row = newUnit(rowCtx, { name: u.name, symbol: u.symbol, allows_decimal: u.allowsDecimal })
    unitIds.set(u.key, row.id)
    first.push(insertOp('units', row))
  }
  const brandIds = new Map<string, string>()
  for (const name of plan.newBrands) {
    const key = name.trim().toLowerCase()
    if (!ctx.createBrands?.has(key)) continue
    const brand = newBrand(rowCtx, { name: name.trim() })
    brandIds.set(key, brand.id)
    first.push(insertOp('brands', brand))
  }
  for (const name of plan.newCategories) {
    const key = name.trim().toLowerCase()
    if (!ctx.createCategories.has(key)) continue
    const cat = newCategory(rowCtx, { name: name.trim() })
    categoryIds.set(key, cat.id)
    first.push(insertOp('categories', cat))
  }

  // Main products of variant rows: created, or turned into groups, in the first chunk.
  const groupIds = new Map<string, string>()
  const groupRows = new Map<string, ParsedProduct[]>()
  for (const row of plan.rows) {
    const v = row.values
    if (row.status !== 'create' || !v?.groupCode || !v.variantValues) continue
    const key = v.groupCode.trim().toLowerCase()
    groupRows.set(key, [...(groupRows.get(key) ?? []), v])
  }
  for (const [key, members] of groupRows) {
    const firstRow = members[0]!
    let options: VariantOptions = readOptions(firstRow.group?.variant_options)
    for (const m of members) options = mergeOptions(options, m.variantValues ?? {})
    const flags = {
      kind: 'group' as const,
      track_stock: false,
      track_batches: false,
      track_expiry: false,
      track_serials: false,
      variant_options: options,
    }
    if (firstRow.group) {
      groupIds.set(key, firstRow.group.id)
      first.push(updateOp('products', firstRow.group, flags))
      continue
    }
    const g = newProduct(rowCtx, {
      sku: firstRow.groupCode!.trim(),
      name: firstRow.groupName ?? firstRow.name,
      category_id:
        firstRow.categoryId ??
        (firstRow.categoryName
          ? (categoryIds.get(firstRow.categoryName.trim().toLowerCase()) ?? null)
          : null),
      unit_id: firstRow.newUnitKey ? (unitIds.get(firstRow.newUnitKey) ?? null) : firstRow.unitId,
      tax_rate_id: firstRow.taxRateId,
      sell_price_paisa: firstRow.sellPricePaisa,
      cost_price_paisa: firstRow.costPricePaisa,
      price_includes_tax: firstRow.priceIncludesTax,
      brand_id:
        firstRow.brandId ??
        (firstRow.brandName
          ? (brandIds.get(firstRow.brandName.trim().toLowerCase()) ?? null)
          : null),
      ...flags,
    })
    groupIds.set(key, g.id)
    first.push(insertOp('products', g))
  }

  const chunks: SyncOp[][] = []
  let current: SyncOp[] = first
  let inChunk = 0
  const flush = () => {
    if (current.length > 0) chunks.push(current)
    current = []
    inChunk = 0
  }

  for (const row of plan.rows) {
    if (row.status !== 'create' && row.status !== 'update') continue
    const v = row.values
    if (!v) continue
    const newCategoryId =
      v.categoryName && !v.categoryId
        ? (categoryIds.get(v.categoryName.trim().toLowerCase()) ?? null)
        : null

    const unitId = v.newUnitKey ? (unitIds.get(v.newUnitKey) ?? null) : v.unitId
    const newBrandId =
      v.brandName && !v.brandId ? (brandIds.get(v.brandName.trim().toLowerCase()) ?? null) : null

    if (row.status === 'create') {
      const product = newProduct(rowCtx, {
        sku: v.sku,
        name: v.name,
        barcode: v.barcode,
        description: v.description,
        category_id: v.categoryId ?? newCategoryId,
        unit_id: unitId,
        tax_rate_id: v.taxRateId,
        sell_price_paisa: v.sellPricePaisa,
        cost_price_paisa: v.costPricePaisa,
        price_includes_tax: v.priceIncludesTax,
        min_stock_milli: v.minStockMilli ?? 0,
        brand_id: v.brandId ?? newBrandId,
        manufacturer: v.manufacturer,
        mrp_paisa: v.mrpPaisa,
        wholesale_price_paisa: v.wholesalePricePaisa,
        min_price_paisa: v.minPricePaisa,
        plu_code: v.pluCode,
        hs_code: v.hsCode,
        shelf_location: v.shelfLocation,
        tags: v.tags,
        attributes: v.attributes,
        ...(v.batchNo ? { track_batches: true, track_expiry: v.expiryDate !== null } : {}),
        ...(v.groupCode && v.variantValues
          ? {
              kind: 'variant' as const,
              parent_id: groupIds.get(v.groupCode.trim().toLowerCase()) ?? null,
              variant_values: v.variantValues,
            }
          : {}),
      })
      current.push(insertOp('products', product))
      let batchId: string | null = null
      if (v.batchNo) {
        const batch = batchOp(
          rowCtx,
          product.id,
          {
            batchNo: v.batchNo,
            mfgDate: v.mfgDate,
            expiryDate: v.expiryDate,
            mrpPaisa: v.mrpPaisa,
            costPaisa: v.costPricePaisa,
          },
          null,
        )
        batchId = batch.id
        if (batch.op) current.push(batch.op)
      }
      if (ctx.locationId && v.openingMilli && v.openingMilli > 0) {
        current.push(
          insertOp(
            'stock_movements',
            newMovement(rowCtx, {
              product_id: product.id,
              location_id: ctx.locationId,
              type: 'opening',
              qty_milli: v.openingMilli,
              unit_cost_paisa: v.costPricePaisa,
              batch_id: batchId,
              notes: 'Opening stock (import)',
            }),
          ),
        )
      }
    } else if (row.existing) {
      const changes: Record<string, unknown> = { ...row.changes }
      if (newCategoryId) changes.category_id = newCategoryId
      if (v.newUnitKey && unitId) changes.unit_id = unitId
      if (newBrandId) changes.brand_id = newBrandId
      if (Object.keys(changes).length === 0) continue
      current.push(updateOp('products', row.existing, changes))
    }

    inChunk++
    if (inChunk >= chunkSize) flush()
  }
  flush()
  return chunks
}
